import { useI18n } from 'vue-i18n'

import { getApiErrorMessage } from '@common/core-layer/utils/api-error-message'
import { isHttpNotFoundError } from '@common/core-layer/utils/http-status.utils'
import { useNotificationHelper } from '@common/core-layer/utils/toast.utils'

/**
 * Surfaces chat failures as toasts.
 *
 * Use only from components and composables that render inside the notification provider
 * (`HLContentWrap`). `useNotificationHelper` resolves the Naive notification API through
 * `inject()`, which only works during the setup of a component below that provider. Keeping it
 * out of `useChats` is what lets the app shell — which mounts the provider in its own template —
 * call the chats layer without throwing out of setup and turning every SSR route into a 500.
 */
export const useChatErrorToast = () => {
  const { t } = useI18n()
  const { showError } = useNotificationHelper()

  /**
   * Toast the reason a direct chat could not be started.
   *
   * @returns `true` when a chat-specific message was shown, so callers can fall back to their own
   * copy for failures this layer has no message for.
   */
  const notifyCreateConversationFailure = (error: unknown): boolean => {
    if (!isHttpNotFoundError(error)) {
      return false
    }

    // This English locale value is a backend response contract used for matching, not display copy.
    const accessDeniedApiMessage = t('chats.backendMessages.createConversationAccessDenied', {}, { locale: 'en' })
    const isAccessDenied = getApiErrorMessage(error).includes(accessDeniedApiMessage)

    showError({ title: t(isAccessDenied ? 'chats.errors.recipientChatDisabled' : 'chats.errors.unableToStartConversation') })
    return true
  }

  return { notifyCreateConversationFailure }
}
